import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ticketsService from "../services/ticketsService";
import Loader from "../components/Loader";
import "../assets/panels.css";

const TicketNoteView = () => {
  const { id, noteId } = useParams();
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingDocument, setDownloadingDocument] = useState(null);

  useEffect(() => {
    const fetchNote = async () => {
      try {
        setLoading(true);
        const noteData = await ticketsService.getTicketNote(noteId);
        setNote(noteData);
        setError(null);
      } catch (err) {
        setError(err.message || "Failed to load note");
      } finally {
        setLoading(false);
      }
    };

    fetchNote();
  }, [noteId]);

  const handleDownloadDocument = async (doc) => {
    try {
      setDownloadingDocument(doc.id);
      console.log('📥 Downloading document via API:', doc.id);

      // Use the same download endpoint as ticket documents
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/documents/${doc.id}/download`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      setError(null);
      console.log('✅ Document downloaded successfully');
    } catch (err) {
      console.error("Download error:", err);
      setError("Failed to download document: " + err.message);
    } finally {
      setDownloadingDocument(null);
    }
  };

  const handleOpenDocument = async (doc) => {
    try {
      console.log('🔍 Opening document via API:', doc.id);

      // Use the same download endpoint but open in new tab
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/documents/${doc.id}/download`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Create blob URL and open in new tab
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');

      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 100);

      setError(null);
      console.log('✅ Document opened successfully');
    } catch (err) {
      console.error("Error opening document:", err);
      setError("Failed to open document: " + err.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) return <Loader />;

  const responsiveStyles = `
    .ticket-note-view-container {
      max-width: 1400px;
      margin: 0 auto;
      padding: 32px 120px;
    }

    .ticket-note-content {
      display: flex;
      gap: 32px;
      align-items: flex-start;
    }

    .ticket-note-left-column {
      flex: 2;
      min-width: 0;
    }

    .ticket-note-right-column {
      width: 400px;
    }

    .ticket-note-description dt {
      color: #5cb85c;
      font-weight: normal;
      float: left;
      width: 100px;
    }

    .ticket-note-description dd {
      margin-left: 110px;
    }

    .ticket-note-actions {
      display: flex;
      flex-direction: row;
      gap: 6px;
    }

    @media (max-width: 1024px) {
      .ticket-note-view-container {
        padding: 24px 40px;
      }

      .ticket-note-right-column {
        width: 300px;
      }
    }

    @media (max-width: 768px) {
      .ticket-note-view-container {
        padding: 16px 20px;
      }

      .ticket-note-content {
        flex-direction: column;
        gap: 20px;
      }

      .ticket-note-left-column {
        width: 100%;
      }

      .ticket-note-right-column {
        width: 100%;
      }

      .ticket-note-description {
        display: flex;
        flex-direction: row;
        gap: 8px;
      }

      .ticket-note-description dt {
        float: none;
        width: auto;
        min-width: auto;
      }

      .ticket-note-description dd {
        margin-left: 0;
        flex: 1;
      }

      .ticket-note-view-container .ticket_details_items {
        text-align: left !important;
        justify-content: flex-start !important;
        gap: 8px !important;
      }

      .ticket-note-view-container .ticket_details_items b,
      .ticket-note-view-container .ticket_details_items span {
        text-align: left !important;
      }

      .ticket-note-view-container .ticket_details_items b {
        min-width: auto !important;
        width: auto !important;
      }
    }

    @media (max-width: 576px) {
      .ticket-note-view-container {
        padding: 12px 10px;
      }

      .ticket-note-actions {
        flex-direction: column;
        gap: 8px;
      }

      .ticket-note-actions .btn {
        width: 100%;
      }

      .ticket-note-view-container .table th,
      .ticket-note-view-container .table td {
        padding: 8px 6px;
        font-size: 13px;
      }

      .ticket-note-view-container .breadcrumb-row {
        flex-wrap: wrap;
        gap: 4px;
      }
    }
  `;

  return (
    <>
      <style>{responsiveStyles}</style>
      <div className="container-fluid ticket-note-view-container">
      {/* Breadcrumb */}
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/tickets')}
        >
          <span className="breadcrumb-sep">/</span>
          Tickets
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate(`/tickets/${id}`)}
        >
          <span className="breadcrumb-sep">/</span>
          Ticket {id}
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Note {noteId}</span>
        </div>
      </div>

      {/* Back Button */}
      <button
        onClick={() => navigate(`/tickets/${id}`)}
        className="btn-action show mb-3"
      >
        ← Back to Ticket
      </button>

      {error && (
        <div className="alert alert-danger mb-3">
          {error}
        </div>
      )}

      {note && (
        <div className="ticket-note-content">
          {/* Left Column: Note Details */}
          <div className="ticket-note-left-column">
            {/* Note Details Panel */}
            <div className="panel mb-4">
              <div className="panel-title">
                <h3>Ticket Note Details</h3>
              </div>
              <div className="panel-body">
                <div style={{ marginBottom: 16 }}>
                  <div className="ticket_details_items">
                    <b>Note ID:</b>
                    <span>{note.id}</span>
                  </div>
                  <div className="ticket_details_items">
                    <b>Ticket ID:</b>
                    <span>{note.ticket_id}</span>
                  </div>
                  <div className="ticket_details_items">
                    <b>Created:</b>
                    <span>
                      {note.created_at
                        ? new Date(note.created_at).toLocaleDateString()
                        : note.date_created
                        ? new Date(note.date_created).toLocaleDateString()
                        : "N/A"}
                      {note.user && ` by ${note.user.first_name} ${note.user.last_name}`}
                    </span>
                  </div>
                  <div className="ticket_details_items">
                    <b>Updated:</b>
                    <span>
                      {note.updated_at
                        ? new Date(note.updated_at).toLocaleDateString()
                        : note.date_updated
                        ? new Date(note.date_updated).toLocaleDateString()
                        : "N/A"}
                      {note.user && ` by ${note.user.first_name} ${note.user.last_name}`}
                    </span>
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <dl className="list ticket-note-description" style={{ margin: 0 }}>
                    <dt>Description:</dt>
                    <dd
                      dangerouslySetInnerHTML={{
                        __html: note.description
                          ? note.description.replace(/\n/g, '<br>')
                          : "N/A",
                      }}
                    />
                  </dl>
                </div>
              </div>
            </div>

            {/* Documents Panel */}
            <div className="panel mb-4">
              <div className="panel-title">
                <h3>Attachments</h3>
              </div>
              <div className="panel-body">
                {note.documents && note.documents.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover table-condensed">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Uploaded</th>
                          <th style={{ textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {note.documents.map((doc) => (
                          <tr key={doc.id}>
                            <td>
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleOpenDocument(doc);
                                }}
                                className="text-primary"
                              >
                                {doc.name}
                              </a>
                            </td>
                            <td>
                              {doc.date_uploaded
                                ? new Date(doc.date_uploaded).toLocaleDateString()
                                : "-"}
                            </td>
                            <td>
                              <div className="ticket-note-actions">
                                <button
                                  onClick={() => handleDownloadDocument(doc)}
                                  className="btn btn-sm btn-warning"
                                  disabled={downloadingDocument === doc.id}
                                >
                                  {downloadingDocument === doc.id ? "Downloading..." : "Download"}
                                </button>
                                <button
                                  onClick={() => handleOpenDocument(doc)}
                                  className="btn btn-sm"
                                  style={{
                                    backgroundColor: '#1B2046',
                                    color: '#fff',
                                    border: 'none'
                                  }}
                                >
                                  Open
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="form-info">
                    No attachments found for this note.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Meta Info */}
          <div className="ticket-note-right-column">
            <div className="panel mb-4">
              <div className="panel-title">
                <h3>Note Information</h3>
              </div>
              <div className="panel-body">
                <div className="ticket_details_items">
                  <b>Note Type:</b>
                  <span>Ticket Note</span>
                </div>
                <div className="ticket_details_items">
                  <b>Total Attachments:</b>
                  <span>{note.documents ? note.documents.length : 0}</span>
                </div>
                <div className="ticket_details_items">
                  <b>Status:</b>
                  <span>Active</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default TicketNoteView; 